import React, { useState, useRef, useEffect } from "react";
import { Box, CardMedia, IconButton } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";


export default function ProductImage({
  images,
  title,
  disableGallery = false,
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const isSwiping = useRef(false);

  const [imageError, setImageError] = useState(false);

  const minSwipeDistance = 40;

  const getImageUrl = (imgInput) => {
    const no_image_var = "عکس یافت نشد!";

    if (!imgInput) {
      return `https://via.placeholder.com/400x400?text=${encodeURIComponent(no_image_var)}`;
    }

    const url =
      typeof imgInput === "string" ? imgInput : imgInput.image || imgInput.url;

    if (!url) {
      return `https://via.placeholder.com/400x400?text=${no_image_var}`;
    }

    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }

    const apiBase = process.env.REACT_APP_API_URL || "http://localhost:9000";
    const cleanBase = apiBase.replace(/\/$/, "");
    const cleanPath = url.startsWith("/") ? url : `/${url}`;

    return `${cleanBase}${cleanPath}`;
  };

  // If gallery is disabled, treat it as only having a single image
  const hasMultipleImages = !disableGallery && images && images.length > 1;
  const currentImage =
    images && images.length > 0
      ? images[disableGallery ? 0 : currentIndex]
      : null;

  const imageUrl = currentImage ? getImageUrl(currentImage) : null;

  useEffect(() => {
    setImageError(false);
  }, [currentImage]);

  const handleNext = (e) => {
    if (disableGallery) return;
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handlePrev = (e) => {
    if (disableGallery) return;
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleDotClick = (e, index) => {
    if (disableGallery) return;
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCurrentIndex(index);
  };

  // Touch handlers for image swiping
  const onTouchStart = (e) => {
    if (disableGallery) return;
    isSwiping.current = false;
    touchStartX.current = e.targetTouches[0].clientX;
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e) => {
    if (disableGallery) return;
    touchEndX.current = e.targetTouches[0].clientX;
    if (Math.abs(touchStartX.current - touchEndX.current) > 10) {
      isSwiping.current = true;
    }
  };

  const onTouchEnd = (e) => {
    if (disableGallery) return;
    const distance = touchStartX.current - touchEndX.current;

    if (Math.abs(distance) > minSwipeDistance) {
      e.stopPropagation();
      if (distance > 0) {
        handleNext(e);
      } else {
        handlePrev(e);
      }
    }
  };

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      sx={{
        width: "100%",
        aspectRatio: "1 / 1",
        minHeight: 0,
        position: "relative",
        bgcolor: "#fff",
        overflow: "hidden",
        direction: "rtl",
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTapHighlightColor: "transparent",
        touchAction: disableGallery ? "auto" : "pan-y",
        "& *": {
          WebkitTapHighlightColor: "transparent",
        },
      }}
    >
      {currentImage && !imageError && (
        <CardMedia
          component="img"
          image={imageUrl}
          alt={title || "Product"}
          draggable={false}
          onError={() => setImageError(true)}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            objectPosition: "center",
            p: 0,
            m: 0,
            boxSizing: "border-box",
            display: "block",
          }}
        />
      )}

      {(!currentImage || imageError) && (
        <Box
          dir="rtl"
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#f3f4f6",
            color: "#666",
            fontSize: { xs: 12, sm: 14 },
            textAlign: "center",
          }}
        >
          عکس یافت نشد!
        </Box>
      )}

      {/* Navigation Arrows */}
      {hasMultipleImages && (
        <>
          <IconButton
            size="small"
            onClick={handlePrev}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => {
              e.stopPropagation();
              handlePrev(e);
            }}
            disableRipple
            disableFocusRipple
            sx={{
              position: "absolute",
              top: "50%",
              left: 8,
              transform: "translateY(-50%)",
              bgcolor: "rgba(255, 255, 255, 0.9)",
              boxShadow: 2,
              opacity: { xs: 0.85, md: isHovered ? 1 : 0 },
              transition: "opacity 0.2s ease-in-out",
              zIndex: 3,
              outline: "none",
              WebkitTapHighlightColor: "transparent",
              "&:hover": { bgcolor: "#fff" },
              "&:active": { bgcolor: "rgba(255, 255, 255, 0.9)" },
            }}
          >
            <ChevronLeftIcon fontSize="small" color="action" />
          </IconButton>

          <IconButton
            size="small"
            onClick={handleNext}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => {
              e.stopPropagation();
              handleNext(e);
            }}
            disableRipple
            disableFocusRipple
            sx={{
              position: "absolute",
              top: "50%",
              right: 8,
              transform: "translateY(-50%)",
              bgcolor: "rgba(255, 255, 255, 0.9)",
              boxShadow: 2,
              opacity: { xs: 0.85, md: isHovered ? 1 : 0 },
              transition: "opacity 0.2s ease-in-out",
              zIndex: 3,
              outline: "none",
              WebkitTapHighlightColor: "transparent",
              "&:hover": { bgcolor: "#fff" },
              "&:active": { bgcolor: "rgba(255, 255, 255, 0.9)" },
            }}
          >
            <ChevronRightIcon fontSize="small" color="action" />
          </IconButton>
        </>
      )}

      {/* Pagination Dots */}
      {hasMultipleImages && (
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 0.5,
            zIndex: 3,
            WebkitTapHighlightColor: "transparent",
          }}
        >
          {images.map((_, idx) => (
            <Box
              key={idx}
              onClick={(e) => handleDotClick(e, idx)}
              onTouchStart={(e) => e.stopPropagation()}
              onTouchEnd={(e) => {
                e.stopPropagation();
                handleDotClick(e, idx);
              }}
              sx={{
                p: 1,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              <Box
                sx={{
                  width: idx === currentIndex ? 8 : 6,
                  height: idx === currentIndex ? 8 : 6,
                  borderRadius: "50%",
                  bgcolor: idx === currentIndex ? "primary.main" : "grey.400",
                  opacity: idx === currentIndex ? 1 : 0.6,
                  transition: "all 0.2s ease",
                }}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
